"use client";

import Image from "next/image";
import React, { useEffect, useId, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useOutsideClick } from "@/hooks/use-outside-click";
import { BsThreeDots } from "@/components/icons";
import {
  lightPlatformIcons,
  platformIcons,
  platformNames,
} from "@/utils/platform-specific-data";
import { addAffiliateParams } from "@/utils/affiliate-links";
import { slugifyReleaseName } from "@/lib/artistUtils";

interface Props {
  cards: {
    artistHandle: string;
    short_id: string;
    id: string;
    description: string;
    title: string;
    duration: string;
    src: string;
    ctaText: string;
    releaseDate: string;
    links: {
      platform:
        | "spotify"
        | "soundcloud"
        | "beatport"
        | "youtube"
        | "apple_music";
      url: string;
    }[];
    content: React.ReactNode | (() => React.ReactNode);
  }[];
}

export default function TrackItems({ cards }: Props) {
  const [active, setActive] = useState<(typeof cards)[number] | boolean | null>(
    null
  );
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setActive(false);
      }
    }

    if (active && typeof active === "object") {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [active]);

  useOutsideClick(ref, () => setActive(null));

  const handleModalOpen = (
    e: React.MouseEvent,
    card: (typeof cards)[number]
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setActive(card);
  };

  const isNewRelease = (releaseDate: string) => {
    const today = new Date();
    const release = new Date(releaseDate);
    const diffTime = Math.abs(today.getTime() - release.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays < 30;
  };

  return (
    <>
      <AnimatePresence>
        {active && typeof active === "object" && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-overlay/20 h-full w-full z-10"
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {active && typeof active === "object" ? (
          <div className="fixed inset-0 z-[100] overflow-y-auto md:flex md:items-start md:justify-center md:p-4">
            <motion.button
              key={`button-${active.title}-${id}`}
              layout
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              exit={{
                opacity: 0,
                transition: {
                  duration: 0.05,
                },
              }}
              className="flex fixed top-2 right-2 md:absolute md:top-6 md:right-6 lg:hidden items-center justify-center bg-theme-neon border-1 border-line-strong rounded-full h-6 w-6 z-10"
              onClick={() => setActive(null)}
            >
              <CloseIcon />
            </motion.button>
            <motion.div
              layoutId={`card-${active.title}-${id}`}
              ref={ref}
              className="w-full min-h-full md:h-auto md:max-w-[500px] md:min-h-fit md:my-8 flex flex-col bg-surface-raised dark:bg-surface md:rounded-3xl md:shadow-xl"
            >
              <motion.div layoutId={`image-${active.title}-${id}`}>
                <Image
                  priority
                  width={1000}
                  height={1000}
                  src={active.src}
                  alt={active.title}
                  className="w-full md:rounded-tr-lg md:rounded-tl-lg object-cover object-top"
                />
              </motion.div>

              <div className="p-4">
                <div className="flex justify-between items-start">
                  <div className="">
                    <motion.h3
                      layoutId={`title-${active.title}-${id}`}
                      className="text-sm hover:underline cursor-pointer"
                    >
                      {active.title}
                    </motion.h3>
                  </div>

                  <motion.a
                    layoutId={`button-${active.title}-${id}`}
                    href={`/track/${slugifyReleaseName(active.title)}/${active.short_id}`}
                    className="px-2 py-1 rounded-full font-bold bg-theme-neon text-on-accent text-xs"
                  >
                    {active.ctaText}
                  </motion.a>
                </div>

                <div className="relative">
                  <motion.div
                    layout
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="text-subtle text-xs py-4 flex flex-col items-start dark:text-muted"
                  >
                    {typeof active.content === "function"
                      ? active.content()
                      : active.content}
                  </motion.div>
                </div>

                <div className="flex flex-col items-center justify-center gap-4 mt-4 pb-8">
                  {active.links
                    .sort((a, b) => {
                      // Beatport always first
                      if (a.platform === "beatport") return -1;
                      if (b.platform === "beatport") return 1;
                      // Then sort alphabetically
                      return platformNames[a.platform].localeCompare(
                        platformNames[b.platform]
                      );
                    })
                    .map((link) => (
                      <motion.a
                        key={`${link.platform}-${link.url}`}
                        layoutId={`button-${link.platform}-${link.url}-${active.title}-${id}`}
                        href={addAffiliateParams(link.url, link.platform)}
                        target="_blank"
                        rel={
                          link.platform === "beatport"
                            ? "nofollow sponsored"
                            : undefined
                        }
                        aria-label={`Listen on ${platformNames[link.platform]}`}
                        className="h-10 w-full flex space-x-2 text-center items-center justify-center px-2 py-1 rounded-md font-bold bg-theme-neon text-on-accent text-xs"
                      >
                        {platformIcons[link.platform]}
                        <span>{platformNames[link.platform]}</span>
                      </motion.a>
                    ))}
                </div>
              </div>
            </motion.div>
          </div>
        ) : null}
      </AnimatePresence>

      {/* <ul className="max-w-2xl mx-auto w-full ga"> */}
      {cards.map((card, index) => (
        <motion.div
          layoutId={`card-${card.title}-${id}`}
          key={`card-${card.title}-${id}`}
          onClick={(e) => handleModalOpen(e, card)}
          className="flex items-center justify-between space-x-2 md:space-x-4 py-3 border-b border-line-strong dark:border-line-strong cursor-pointer hover:bg-surface-raised dark:hover:bg-surface-raised/50 transition-colors"
        >
          <div className="flex items-center gap-4 sm:gap-7 flex-[100%] md:flex-[50%]">
            <motion.div
              className="text-light"
              layoutId={`index-${card.title}-${id}`}
            >
              {index + 1}.
            </motion.div>
            <motion.div
              className="relative h-10 w-10"
              layoutId={`image-${card.title}-${id}`}
            >
              <Image
                fill
                sizes="(min-width: 768px) 112px, 80px"
                src={card.src}
                alt={card.title}
                className="h-10 w-10 md:h-14 md:w-14 object-cover object-top rounded"
              />
              {isNewRelease(card.releaseDate) && (
                <span className="absolute bottom-0 right-0 left-0 text-xxxxs text-center w-full bg-opacity-60 bg-theme-neon font-semibold text-on-accent rounded-none">
                  New
                </span>
              )}
            </motion.div>
            <div className="flex flex-col justify-center">
              <motion.h3
                layoutId={`title-${card.title}-${id}`}
                className="text-[0.82rem] md:text-sm hover:underline"
              >
                <a
                  href={`/track/${slugifyReleaseName(card.title)}/${card.short_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="hover:underline"
                >
                  {card.title}
                </a>
              </motion.h3>
            </div>
          </div>

          <div className="hidden md:flex gap-3 text-xs text-muted flex-[0%] md:flex-[30%]">
            {card.links
              .sort((a, b) => {
                // Beatport always first
                if (a.platform === "beatport") return -1;
                if (b.platform === "beatport") return 1;
                // Then sort alphabetically
                return platformNames[a.platform].localeCompare(
                  platformNames[b.platform]
                );
              })
              .map((link) => (
                <span key={`${link.platform}-${link.url}`}>
                  {lightPlatformIcons[link.platform]}
                </span>
              ))}
          </div>
          <div className="flex space-x-2 flex-[10%] md:flex-[20%] justify-end">
            <motion.p
              layoutId={`duration-${card.duration}-${id}`}
              className="text-muted text-xs"
            >
              {/* 5:21 */}
            </motion.p>
            <motion.button
              layoutId={`button-${card.title}-${id}`}
              aria-label={`Quick preview for ${card.title}`}
              onClick={(e) => handleModalOpen(e, card)}
              className="hover:text-accent transition-colors p-2"
            >
              <BsThreeDots className="text-lg" />
            </motion.button>
          </div>
        </motion.div>
      ))}
    </>
  );
}

export const CloseIcon = () => {
  return (
    <motion.svg
      initial={{
        opacity: 0,
      }}
      animate={{
        opacity: 1,
      }}
      exit={{
        opacity: 0,
        transition: {
          duration: 0.05,
        },
      }}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M18 6l-12 12" />
      <path d="M6 6l12 12" />
    </motion.svg>
  );
};
